import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

const UPDATED_AT = '14 בספטמבר 2026'
const CONTACT_EMAIL = 'office@isternlaw.com'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
  )
}

/**
 * Public page — deliberately outside ProtectedRoute. Google requires a reachable
 * privacy policy URL for an External OAuth app in production, and it is linked from
 * the consent screen, so it must render for visitors who are not signed in.
 */
export function PrivacyPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 4, sm: 8 }, px: 2 }}>
      <Paper sx={{ p: { xs: 3, sm: 5 }, maxWidth: 820, mx: 'auto' }} elevation={2}>
        <Typography variant="h4" component="h1">
          מדיניות פרטיות
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
          מערכת ניהול משרד עורכי דין — יצחק שטרן משרד עורכי דין
        </Typography>
        <Typography variant="caption" color="text.secondary">
          עודכן לאחרונה: {UPDATED_AT}
        </Typography>

        <Divider sx={{ mt: 3 }} />

        <Section title="על מה חלה המדיניות">
          המערכת שבכתובת isternlaw.com היא מערכת פנימית של יצחק שטרן משרד עורכי דין, המשמשת
          את עובדי המשרד לניהול לקוחות, עסקאות ומשימות. היא אינה פתוחה לציבור, והגישה אליה
          ניתנת רק למשתמשים שהוקמו מראש על ידי מנהל המערכת. מדיניות זו מתארת אילו נתונים
          המערכת אוספת וכיצד הם נשמרים.
        </Section>

        <Section title="נתונים שנאספים בעת ההתחברות">
          ההתחברות מתבצעת באמצעות חשבון Google. בתהליך זה המערכת מקבלת מ-Google את השם
          ואת כתובת הדואר האלקטרוני של המשתמש, ומשתמשת בהם כדי לזהות אותו ולשייך את פעולותיו
          במערכת. המערכת אינה מקבלת ואינה שומרת את הסיסמה של חשבון Google. חשבון שאינו מוקם
          מראש על ידי מנהל המערכת נדחה ואינו מקבל גישה לנתונים.
        </Section>

        <Section title="נתונים שהמשתמשים מזינים">
          עובדי המשרד מזינים למערכת נתונים הנדרשים לניהול העבודה: פרטי לקוחות ואנשי קשר,
          פרטי עסקאות ותיקים, משימות ומועדי יעד, תשלומים שהתקבלו ומסמכים הקשורים לתיק.
          נתונים אלה נמסרים על ידי המשרד ובאחריותו, ומשמשים אך ורק לצורך ניהול העבודה השוטפת
          במשרד.
        </Section>

        <Section title="חיבור ל-Google Drive">
          המערכת מאחסנת מסמכים ב-Google Drive של המשרד. לשם כך היא מבקשת הרשאה בהיקף{' '}
          <code>drive.file</code> בלבד — הרשאה המוגבלת לקבצים שהמערכת עצמה יצרה או שהועלו
          דרכה. המערכת אינה יכולה לגשת לקבצים אחרים ב-Drive של המשתמש, לקרוא אותם או למחוק
          אותם. ניתן לנתק את החיבור בכל עת מתוך הגדרות המערכת או מדף ההרשאות בחשבון Google.
        </Section>

        <Section title="עוגיות">
          המערכת עושה שימוש בעוגיה אחת, המשמשת לשמירת ההתחברות של המשתמש בין דפים. אין
          שימוש בעוגיות פרסום, במעקב אחר גולשים או בכלי אנליטיקה של צד שלישי.
        </Section>

        <Section title="היכן הנתונים נשמרים">
          הנתונים נשמרים בבסיס נתונים מנוהל ובשרתי האפליקציה, המתארחים באיחוד האירופי
          (פרנקפורט), וכן ב-Google Drive של המשרד. הגישה אליהם מוגבלת לעובדי המשרד שהורשו
          לכך ולספקי התשתית המארחים, בהתאם לתנאי השירות שלהם.
        </Section>

        <Section title="שיתוף עם צדדים שלישיים">
          המערכת אינה מוכרת נתונים ואינה מעבירה אותם לצדדים שלישיים לצורכי שיווק. העברת
          מידע תיעשה רק לספקי התשתית הנדרשים להפעלת המערכת, או כאשר הדבר נדרש על פי דין.
        </Section>

        <Section title="שמירה ומחיקה">
          נתוני העבודה נשמרים כל עוד הם נדרשים לניהול התיקים ובהתאם לחובות השמירה החלות על
          משרד עורכי דין. לבירור בנוגע לנתונים אישיים או לבקשת מחיקה, ניתן לפנות לכתובת
          שלהלן.
        </Section>

        <Section title="יצירת קשר">
          בשאלות בנוגע למדיניות זו:{' '}
          <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
          <br />
          יצחק שטרן משרד עורכי דין, בר כוכבא 4, בני ברק.
        </Section>

        <Divider sx={{ mt: 4, mb: 2 }} />
        <Link href="/login" variant="body2">
          חזרה למסך ההתחברות
        </Link>
      </Paper>
    </Box>
  )
}
